import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUpdateMe } from "@/api/hooks";

// ─── Themes ───────────────────────────────────────────────────────────────────
export const THEMES = [
  {
    id: "dark",
    label: "כהה",
    description: "מינימליסטי ומקצועי",
    preview: { bg: "#0a0a0a", card: "#111", accent: "#c8f135", text: "#ffffff" },
    light: false,
  },
  {
    id: "purple",
    label: "סגול",
    description: "אנרגטי ומיוחד",
    preview: { bg: "#0d0a1a", card: "#1a1030", accent: "#a78bfa", text: "#ffffff" },
    light: false,
  },
  {
    id: "ocean",
    label: "אוקיינוס",
    description: "רגוע ונקי",
    preview: { bg: "#030f1c", card: "#0a1e30", accent: "#3dffea", text: "#ffffff" },
    light: false,
  },
  {
    id: "light",
    label: "בהיר",
    description: "נקי ואוורירי",
    preview: { bg: "#f5f5f5", card: "#ffffff", accent: "#16a34a", text: "#111111" },
    light: true,
  },
  {
    id: "sunrise",
    label: "סאנרייז",
    description: "חם ואנרגטי",
    preview: { bg: "#fff7ed", card: "#ffffff", accent: "#ea580c", text: "#111111" },
    light: true,
  },
];

export function getThemeVars(themeId) {
  switch (themeId) {
    case "purple":
      return { bg: "#0d0a1a", card: "#1a1030", accent: "#a78bfa", nav: "#110e20", border: "#2a1f4a", textPrimary: "#ffffff", textSecondary: "rgba(255,255,255,0.4)", light: false };
    case "ocean":
      return { bg: "#030f1c", card: "#0a1e30", accent: "#3dffea", nav: "#051525", border: "#0d2d45", textPrimary: "#ffffff", textSecondary: "rgba(255,255,255,0.4)", light: false };
    case "light":
      return { bg: "#f5f5f5", card: "#ffffff", accent: "#16a34a", nav: "#ffffff", border: "#e5e7eb", textPrimary: "#111111", textSecondary: "rgba(0,0,0,0.45)", light: true };
    case "sunrise":
      return { bg: "#fff7ed", card: "#ffffff", accent: "#ea580c", nav: "#ffffff", border: "#fed7aa", textPrimary: "#111111", textSecondary: "rgba(0,0,0,0.45)", light: true };
    default:
      return { bg: "#0a0a0a", card: "#111111", accent: "#c8f135", nav: "#111111", border: "#222222", textPrimary: "#ffffff", textSecondary: "rgba(255,255,255,0.4)", light: false };
  }
}

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  {
    id: "welcome",
    type: "modal", // full-screen modal, no spotlight
  },
  {
    id: "theme",
    type: "modal",
  },
  {
    id: "points",
    type: "spotlight",
    targetSelector: "[data-onboard='points-header']",
    position: "bottom",
    title: "הנקודות שלך 🏆",
    body: "כאן תוכל לראות את ניקוד הנוכחי שלך ואת הדירוג שלך בין כל המשתתפים. ככל שתצבור יותר נקודות — כך תעלה בטבלה!",
  },
  {
    id: "tasks",
    type: "spotlight",
    targetSelector: "[data-onboard='task-list']",
    position: "top",
    title: "המשימות שלך 📋",
    body: "כל משימה מזכה אותך בנקודות. לחץ על משימה כדי להתחיל. משימות בעלות ערך גבוה מסומנות במיוחד!",
  },
  {
    id: "done",
    type: "modal",
  },
];

// ─── Spotlight helpers ────────────────────────────────────────────────────────
function useElementRect(selector, active) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!active || !selector) return;
    const measure = () => {
      const el = document.querySelector(selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [selector, active]);
  return rect;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OnboardingOverlay({ onComplete, themeId, onThemeChange }) {
  const [step, setStep] = useState(0);
  const [localTheme, setLocalTheme] = useState(themeId || "dark");
  const [saving, setSaving] = useState(false);
  const updateMe = useUpdateMe();

  const currentStep = STEPS[step];
  const isSpotlight = currentStep.type === "spotlight";
  const rect = useElementRect(currentStep.targetSelector, isSpotlight);

  const PAD = 12; // spotlight padding around element

  const advance = async () => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      // Final step: save theme + mark onboarding done
      setSaving(true);
      try {
        await updateMe.mutateAsync({ onboarding_done: true, theme: localTheme });
      } catch (err) {
        console.error("Failed to save onboarding preferences:", err);
      }
      onThemeChange(localTheme);
      setSaving(false);
      onComplete();
    }
  };

  const handleThemeSelect = (id) => {
    setLocalTheme(id);
    onThemeChange(id); // live preview
  };

  // Scroll target into view for spotlight steps
  useEffect(() => {
    if (isSpotlight && currentStep.targetSelector) {
      const el = document.querySelector(currentStep.targetSelector);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [step]);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: "all" }}>
        {/* ── Backdrop / cutout ── */}
        {isSpotlight && rect ? (
          <SpotlightBackdrop rect={rect} pad={PAD} />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0"
            style={{ background: "rgba(0,0,0,0.82)", backdropFilter: "blur(2px)" }}
          />
        )}

        {/* ── Tooltip / modal ── */}
        {isSpotlight && rect ? (
          <SpotlightTooltip
            rect={rect}
            pad={PAD}
            step={currentStep}
            stepIndex={step}
            totalSteps={STEPS.length}
            onNext={advance}
          />
        ) : (
          <ModalStep
            step={currentStep}
            stepIndex={step}
            totalSteps={STEPS.length}
            localTheme={localTheme}
            onThemeSelect={handleThemeSelect}
            onNext={advance}
            saving={saving}
          />
        )}
      </div>
    </AnimatePresence>
  );
}

// ─── SVG cutout backdrop ──────────────────────────────────────────────────────
function SpotlightBackdrop({ rect, pad }) {
  const { innerWidth: W, innerHeight: H } = window;
  const x = rect.left - pad;
  const y = rect.top - pad;
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;
  const r = 16;

  return (
    <motion.svg
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: "none" }}
    >
      <defs>
        <mask id="spotlight-mask">
          <rect width={W} height={H} fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={r} fill="black" />
        </mask>
      </defs>
      <rect width={W} height={H} fill="rgba(0,0,0,0.80)" mask="url(#spotlight-mask)" />
      {/* Glow ring */}
      <rect
        x={x} y={y} width={w} height={h} rx={r}
        fill="none"
        stroke="rgba(200,241,53,0.6)"
        strokeWidth="2"
        style={{ filter: "drop-shadow(0 0 8px rgba(200,241,53,0.4))" }}
      />
    </motion.svg>
  );
}

// ─── Spotlight tooltip ────────────────────────────────────────────────────────
function SpotlightTooltip({ rect, pad, step, stepIndex, totalSteps, onNext }) {
  const { innerWidth: W, innerHeight: H } = window;
  const cx = rect.left + rect.width / 2;
  const spottedBottom = rect.bottom + pad;
  const spottedTop = rect.top - pad;

  const below = spottedBottom + 180 < H;
  const tooltipTop = below ? spottedBottom + 16 : spottedTop - 180 - 16;
  const tooltipLeft = Math.max(16, Math.min(W - 340, cx - 170));

  return (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, y: below ? -10 : 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25 }}
      className="absolute rounded-2xl px-5 py-4 shadow-2xl"
      style={{
        top: tooltipTop,
        left: tooltipLeft,
        width: 320,
        background: "#111",
        border: "1px solid rgba(200,241,53,0.35)",
        boxShadow: "0 0 32px rgba(200,241,53,0.15)",
        pointerEvents: "all",
      }}
    >
      <StepDots total={totalSteps} current={stepIndex} />
      <p className="font-black text-white text-base mb-1 mt-2">{step.title}</p>
      <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>{step.body}</p>
      <button
        onClick={onNext}
        className="w-full py-2.5 rounded-xl font-black text-sm text-black transition-all hover:opacity-90"
        style={{ background: "#c8f135" }}
      >
        {stepIndex === totalSteps - 1 ? "בואו נתחיל! 🚀" : "הבא"}
      </button>
    </motion.div>
  );
}

// ─── Full-screen modal step ───────────────────────────────────────────────────
function ModalStep({ step, stepIndex, totalSteps, localTheme, onThemeSelect, onNext, saving }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-4" style={{ pointerEvents: "all" }}>
      <motion.div
        key={step.id}
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 20 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md rounded-3xl p-8 shadow-2xl"
        style={{ background: "#111", border: "1px solid #2a2a2a" }}
      >
        <StepDots total={totalSteps} current={stepIndex} />

        {step.id === "welcome" && <WelcomeContent />}
        {step.id === "theme" && (
          <ThemePickerContent localTheme={localTheme} onThemeSelect={onThemeSelect} />
        )}
        {step.id === "done" && <DoneContent />}

        <button
          onClick={onNext}
          disabled={saving}
          className="w-full mt-6 py-3.5 rounded-xl font-black text-base text-black transition-all hover:opacity-90 disabled:opacity-60"
          style={{ background: "#c8f135" }}
        >
          {saving ? "שומר..." : stepIndex === totalSteps - 1 ? "בואו נתחיל! 🚀" : "הבא →"}
        </button>
      </motion.div>
    </div>
  );
}

function WelcomeContent() {
  return (
    <div className="text-center" dir="rtl">
      <div className="text-5xl mb-4">👋</div>
      <h2 className="font-black text-white text-2xl mb-3">ברוך הבא!</h2>
      <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>
        אנחנו שמחים שאתה פה! בכמה שניות נעבור על כל מה שצריך לדעת כדי להתחיל לצבור נקודות ולנצח כרטיסים לאירוע.
      </p>
    </div>
  );
}

function ThemePickerContent({ localTheme, onThemeSelect }) {
  return (
    <div dir="rtl">
      <div className="text-center mb-6">
        <div className="text-4xl mb-3">🎨</div>
        <h2 className="font-black text-white text-xl mb-1">בחר את הערכת נושא שלך</h2>
        <p className="text-sm" style={{ color: "rgba(255,255,255,0.45)" }}>אפשר לשנות בהמשך</p>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {THEMES.map((t) => {
          const isSelected = localTheme === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onThemeSelect(t.id)}
              className="w-full flex items-center gap-4 rounded-2xl p-3 transition-all text-right"
              style={{
                background: isSelected ? "rgba(200,241,53,0.1)" : "#1a1a1a",
                border: `2px solid ${isSelected ? "#c8f135" : "#2a2a2a"}`,
              }}
            >
              {/* Mini preview */}
              <div
                className="w-10 h-9 rounded-xl flex-shrink-0 overflow-hidden relative"
                style={{ background: t.preview.bg, border: `1px solid ${t.preview.accent}50` }}
              >
                <div className="absolute inset-x-1.5 top-1 h-1.5 rounded-full" style={{ background: t.preview.card }} />
                <div className="absolute bottom-1 left-1.5 w-3 h-1 rounded-full" style={{ background: t.preview.accent }} />
              </div>
              <div className="flex-1">
                <p className="font-black text-white text-sm">{t.label}</p>
                <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>{t.description}</p>
              </div>
              {isSelected && (
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#c8f135" }}>
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="#000" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DoneContent() {
  return (
    <div className="text-center" dir="rtl">
      <div className="text-5xl mb-4">🚀</div>
      <h2 className="font-black text-white text-2xl mb-3">הכל מוכן!</h2>
      <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>
        עכשיו אתה מוכן לצבור נקודות ולהתחרות על כרטיסים לאירוע. בהצלחה!
      </p>
    </div>
  );
}

function StepDots({ total, current }) {
  return (
    <div className="flex justify-center gap-1.5 mb-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="rounded-full transition-all duration-300"
          style={{
            width: i === current ? 20 : 6,
            height: 6,
            background: i === current ? "#c8f135" : "rgba(255,255,255,0.15)",
          }}
        />
      ))}
    </div>
  );
}